import { z } from "zod";
import { type SovLine, formatPercentMilli, percentOf } from "./custom-proposal";

/**
 * Progress billing on a schedule of values, the way a GC's pay application (AIA G702/G703) works.
 * Each line of the schedule has a value; each pay app says, per line, the work completed to date
 * and the materials stored on site now. Retainage is held on everything completed and stored to
 * date (dropping to a lower rate once the work passes a percent complete, if the contract says so),
 * and each pay app bills what's been earned less retainage, less what earlier pay apps billed.
 * Retainage is billed on its own at the end. Whole cents; rounding half up, only here.
 */

/** One schedule line as the pay app sees it: the proposal's lines, then approved change orders. */
export type ScheduleLine = SovLine;

/** What the rep enters for a line: work completed to date (cumulative, installed) and materials stored now. */
export interface PayAppEntry {
  key: string;
  workToDateCents: number;
  storedCents: number;
}

/** A line of the continuation sheet (G703). */
export interface PayAppLine {
  key: string;
  description: string;
  /** C: scheduled value. */
  valueCents: number;
  /** D: work completed on earlier pay apps. */
  previousCents: number;
  /** E: work completed this period. */
  thisPeriodCents: number;
  /** F: materials presently stored (not in D or E). */
  storedCents: number;
  /** G: completed and stored to date (D + E + F). */
  toDateCents: number;
  /** G ÷ C, thousandths of a percent (null for a $0 line). */
  percentMilli: number | null;
  /** H: balance to finish (C − G). */
  balanceCents: number;
}

/** The summary (G702). */
export interface PayAppSummary {
  /** 1: the signed contract (with the options picked). */
  originalCents: number;
  /** 2: approved change orders. */
  changeOrdersCents: number;
  /** 3: contract sum to date (1 + 2). */
  contractToDateCents: number;
  /** 4: total completed and stored to date. */
  completedToDateCents: number;
  /** The rate used this time (after any step-down). */
  retainagePercentMilli: number;
  /** 5: retainage held to date. */
  retainageCents: number;
  /** 6: total earned less retainage (4 − 5). */
  earnedLessRetainageCents: number;
  /** 7: less previous certificates for payment (line 6 of the last pay app). */
  previousCertificatesCents: number;
  /** 8: current payment due (6 − 7). */
  currentDueCents: number;
  /** 9: balance to finish, including retainage (3 − 6). */
  balanceToFinishCents: number;
  /** Work installed to date ÷ contract to date (what the step-down looks at), thousandths of a percent. */
  workPercentMilli: number;
}

export interface Retainage {
  retainagePercentMilli: number;
  stepDown: { atCompletePercent: number; toPercentMilli: number } | null;
}

/** What the last pay app left: each line's work to date (D for the next one), and its line 6. */
export interface PreviousPayApp {
  workToDate: Record<string, number>;
  earnedLessRetainageCents: number;
  /** Line 4 of the last one: this pay app's gross for the period is the increase over it. */
  completedToDateCents: number;
  retainageCents: number;
}

export const NO_PREVIOUS: PreviousPayApp = { workToDate: {}, earnedLessRetainageCents: 0, completedToDateCents: 0, retainageCents: 0 };

const within = (n: number, value: number) => (value >= 0 ? n >= 0 && n <= value : n <= 0 && n >= value);

/** Works out a pay app. `problems` says why it can't be billed (empty when it can). */
export function computePayApp(schedule: readonly ScheduleLine[], originalCents: number, entries: readonly PayAppEntry[], prev: PreviousPayApp, r: Retainage): { lines: PayAppLine[]; summary: PayAppSummary; problems: string[] } {
  const problems: string[] = [];
  const byKey = new Map(entries.map((e) => [e.key, e]));
  const lines: PayAppLine[] = schedule.map((s) => {
    const e = byKey.get(s.key);
    const previous = prev.workToDate[s.key] ?? 0;
    const work = e?.workToDateCents ?? previous;
    const stored = e?.storedCents ?? 0;
    if (!within(work, s.valueCents)) problems.push(`${s.description}: work to date has to be between $0 and its value.`);
    else if (!within(work + stored, s.valueCents)) problems.push(`${s.description}: work and stored materials are more than its value.`);
    if (s.valueCents >= 0 ? work < previous : work > previous) problems.push(`${s.description}: less than the last pay app billed (${(previous / 100).toFixed(2)}).`);
    const toDate = work + stored;
    return {
      key: s.key,
      description: s.description,
      valueCents: s.valueCents,
      previousCents: previous,
      thisPeriodCents: work - previous,
      storedCents: stored,
      toDateCents: toDate,
      percentMilli: s.valueCents ? Math.round((toDate / s.valueCents) * 100_000) : null,
      balanceCents: s.valueCents - toDate,
    };
  });
  for (const e of entries) if (!schedule.some((s) => s.key === e.key)) problems.push("A line isn't on the schedule of values anymore. Start the pay app again.");
  const scheduleCents = schedule.reduce((t, s) => t + s.valueCents, 0);
  const changeOrdersCents = scheduleCents - originalCents;
  const completed = lines.reduce((t, l) => t + l.toDateCents, 0);
  const work = lines.reduce((t, l) => t + l.toDateCents - l.storedCents, 0);
  const workPercentMilli = scheduleCents > 0 ? Math.floor((work / scheduleCents) * 100_000) : 0;
  const rate = r.stepDown && workPercentMilli >= r.stepDown.atCompletePercent * 1000 ? r.stepDown.toPercentMilli : r.retainagePercentMilli;
  const retainageCents = percentOf(completed, rate);
  const earned = completed - retainageCents;
  const currentDueCents = earned - prev.earnedLessRetainageCents;
  if (currentDueCents <= 0) problems.push("Nothing new to bill: enter the work completed since the last pay app.");
  return {
    lines,
    summary: {
      originalCents,
      changeOrdersCents,
      contractToDateCents: scheduleCents,
      completedToDateCents: completed,
      retainagePercentMilli: rate,
      retainageCents,
      earnedLessRetainageCents: earned,
      previousCertificatesCents: prev.earnedLessRetainageCents,
      currentDueCents,
      balanceToFinishCents: scheduleCents - earned,
      workPercentMilli,
    },
    problems,
  };
}

/**
 * The pay app's invoice lines: the work completed and stored this period, and the retainage held
 * (or released, after a step-down) this period. They add up to the current payment due.
 */
export function payAppInvoiceLines(number: number, periodTo: string, s: PayAppSummary, prev: PreviousPayApp): { description: string; amountCents: number; retainage?: boolean }[] {
  const gross = s.completedToDateCents - prev.completedToDateCents;
  const held = s.retainageCents - prev.retainageCents;
  const out: { description: string; amountCents: number; retainage?: boolean }[] = [{ description: `Pay application #${number}, period to ${periodTo}: work completed and stored this period`, amountCents: gross }];
  if (held > 0) out.push({ description: `Less retainage held (${formatPercentMilli(s.retainagePercentMilli)})`, amountCents: -held, retainage: true });
  if (held < 0) out.push({ description: `Retainage released (now ${formatPercentMilli(s.retainagePercentMilli)})`, amountCents: -held, retainage: true });
  return out;
}

/** "Oct 31, 2026" → kept as typed; the period end is a date. */
export const payAppInputSchema = z.object({
  periodTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick the period's end date"),
  entries: z
    .array(z.object({ key: z.string().min(1).max(80), workToDateCents: z.number().int().min(-1_000_000_000).max(1_000_000_000), storedCents: z.number().int().min(-1_000_000_000).max(1_000_000_000).default(0) }))
    .max(200),
});

/** A pay app as the CRM keeps it. */
export interface PayAppItem {
  id: string;
  number: number;
  periodTo: string;
  status: "billed" | "void";
  lines: PayAppLine[];
  summary: PayAppSummary;
  invoice: { id: string; number: number; status: string } | null;
  pdfUrl: string | null;
  createdAt: string;
}

/** The job's progress billing on one signed proposal. */
export interface ProgressBilling {
  proposalId: string;
  title: string;
  /** The schedule now: the proposal's lines, then approved change orders. */
  schedule: ScheduleLine[];
  originalCents: number;
  retainage: Retainage;
  storedMaterials: boolean;
  terms: string;
  payApps: PayAppItem[];
  /** What the next pay app starts from. */
  previous: PreviousPayApp;
  nextNumber: number;
  /** Retainage held so far, and its release invoice once made. */
  retainageHeldCents: number;
  release: { id: string; number: number; status: string } | null;
}
